import type { AuditEvent } from '../api/types';
import { CHILD_ACTION_LABELS, DOCUMENT_CATEGORY_LABELS, EVENT_LABELS, FIELD_LABELS, formatDate, formatMoney, PHOTO_CATEGORY_LABELS, STATUS_LABELS } from './format';

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/** Kurze, verständliche Beschreibung eines Historien-Eintrags. */
export const describeEvent = (e: AuditEvent): string => {
  const m = (e.metadata ?? {}) as Record<string, unknown>;
  const rel = e.related_asset?.asset_number;
  const c = (e.changes ?? {}) as Record<string, { from: unknown; to: unknown }>;
  const status = c.status ? `Status ${STATUS_LABELS[c.status.from as keyof typeof STATUS_LABELS] ?? c.status.from} → ${STATUS_LABELS[c.status.to as keyof typeof STATUS_LABELS] ?? c.status.to}` : null;
  switch (e.event_type) {
    case 'employee_assigned':
      return `An ${m.employee_short_code ?? '?'} (${m.employee_name ?? ''}) übergeben${m.previous_short_code ? `, vorher ${m.previous_short_code}` : ''}`;
    case 'employee_unassigned':
      return `Rückgabe${m.previous_short_code ? ` von ${m.previous_short_code}` : ''}`;
    case 'location_changed':
      return `Standort: ${str(m.previous_location_name) ?? 'unbekannt'} → ${str(m.location_name) ?? 'unbekannt'}`;
    case 'status_changed':
      return [status, str(m.reason)].filter(Boolean).join(' – ');
    case 'component_installed':
      return `Eingebaut in ${m.parent_asset_number ?? rel ?? '?'}`;
    case 'component_moved':
      return m.disposal_number
        ? `Bei Verschrottung ${m.disposal_number} aus ${m.previous_parent_asset_number ?? '?'} in ${m.target_asset_number ?? '?'} umgebaut`
        : `Umgebaut von ${m.previous_parent_asset_number ?? '?'} nach ${m.parent_asset_number ?? rel ?? '?'}`;
    case 'component_removed':
      if (m.disposal_number) {
        const action = CHILD_ACTION_LABELS[String(m.action)] ?? '';
        return `Bei Verschrottung ${m.disposal_number} aus ${m.previous_parent_asset_number ?? '?'} ausgebaut (${action}${m.location_name ? `, ${m.location_name}` : ''})`;
      }
      return `Ausgebaut aus ${m.parent_asset_number ?? rel ?? '?'}${m.location_name ? ` → ${m.location_name}` : ''}${m.reason ? ` – ${m.reason}` : ''}`;
    case 'child_removed':
      if (m.moved_to_asset_number) return `Komponente ${m.child_asset_number} nach ${m.moved_to_asset_number} umgebaut`;
      if (m.reason === 'disposed') return `Komponente ${m.child_asset_number} verschrottet (${m.disposal_number})`;
      return `Komponente ${m.child_asset_number ?? rel ?? ''} entfernt`;
    case 'asset_disposed':
      return `Verschrottet – Vorgang ${m.disposal_number ?? ''}${m.role === 'child' && m.parent_asset_number ? ` (mit ${m.parent_asset_number})` : ''}${m.reason ? `: ${m.reason}` : ''}`;
    case 'document_uploaded':
    case 'photo_uploaded': {
      const cat = (e.event_type === 'photo_uploaded' ? PHOTO_CATEGORY_LABELS : DOCUMENT_CATEGORY_LABELS)[String(m.category)] ?? '';
      return `${cat}: ${m.filename ?? ''}`;
    }
    case 'attachment_deleted':
      return `${m.filename ?? 'Datei'} gelöscht – ${m.reason ?? ''}`;
    case 'part_added':
      return `${m.quantity ?? 1}× ${m.name ?? ''}`;
    case 'part_removed':
      return `${m.quantity ?? 1}× ${m.name ?? ''}${m.reason ? ` – ${m.reason}` : ''}`;
    case 'asset_deleted':
      return `Gelöscht: ${m.reason ?? ''}`;
    case 'asset_created':
      return 'Asset angelegt';
    default:
      return EVENT_LABELS[e.event_type] ?? e.event_type;
  }
};

const MONEY_FIELDS = new Set(['cost_net_cents', 'cost_gross_cents', 'ahk_cents', 'book_value_cents', 'annual_depreciation_cents', 'ahk_fy_end_cents']);
const DATE_FIELDS = new Set(['purchase_date', 'capitalization_date', 'commissioning_date', 'warranty_until', 'ahk_date']);

const fmt = (field: string, v: unknown): string => {
  if (v === null || v === undefined || v === '') return '–';
  if (MONEY_FIELDS.has(field) && typeof v === 'number') return formatMoney(v);
  if (DATE_FIELDS.has(field) && typeof v === 'string') return formatDate(v);
  if (field === 'status') return STATUS_LABELS[v as keyof typeof STATUS_LABELS] ?? String(v);
  if (typeof v === 'boolean') return v ? 'Ja' : 'Nein';
  if (field === 'photo_not_required' || field === 'exclude_from_reconciliation') return v ? 'Ja' : 'Nein';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/** Feldänderungen für die Detailanzeige (IDs werden ausgelassen – die stehen lesbar in der Beschreibung). */
export const changeLines = (e: AuditEvent): Array<{ label: string; from: string; to: string }> => {
  if (!e.changes) return [];
  const skip = new Set(['employee_id', 'location_id', 'parent_asset_id', 'deleted_at']);
  if (e.event_type === 'status_changed') skip.add('status');
  return Object.entries(e.changes as Record<string, { from: unknown; to: unknown }>)
    .filter(([k]) => !skip.has(k))
    .map(([k, v]) => ({ label: FIELD_LABELS[k] ?? k, from: fmt(k, v.from), to: fmt(k, v.to) }));
};
